import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { LeadActivityService } from 'src/app/private/services/lead-activity.service';
import { MODULE_LOADING_MESSAGES } from 'src/app/private/services/module.config';

/** Maps Activities sub-tab id → activity_type prefixes / values */
const FILTER_MAP: Record<string, string[] | null> = {
  activity: null,
  note: ['lead_notes_activity'],
  email: ['lead_communication_activity'],
  task: ['lead_tasks_activity'],
  meeting: ['lead_meeting_activity'],
  call: ['lead_communication_activity'],
  followups: ['lead_followup_activity'],
};

@Component({
  selector: 'app-lead-timeline',
  templateUrl: './lead-timeline.component.html',
  styleUrls: ['./lead-timeline.component.scss'],
})
export class LeadTimelineComponent implements OnChanges, OnDestroy {
  @Input() leadId: number | string | null = null;
  @Input() refreshToken = 0;
  @Input() activityFilter: string = 'activity';

  items: any[] = [];
  allItems: any[] = [];
  
  private _loading = false;
  get loading(): boolean {
    return this._loading;
  }
  set loading(val: boolean) {
    this._loading = val;
    if (val) {
      this.startTimelineMessageRotation();
    } else {
      this.cleanupTimelineMessageRotation();
    }
  }

  timelineActiveMessages: string[] = [];
  timelineCurrentMessageIndex = 0;
  timelineCurrentMessage = '';
  timelineFadeState: 'fade-out' | 'fade-in' | '' = '';
  private timelineMessageInterval: any = null;

  constructor(private leadActivity: LeadActivityService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['leadId'] || changes['refreshToken']) {
      this.load();
    } else if (changes['activityFilter']) {
      this.applyFilter();
    }
  }

  load(): void {
    if (this.leadId == null || this.leadId === '') {
      this.items = [];
      this.allItems = [];
      return;
    }
    this.loading = true;
    this.leadActivity.getAllLeadActivity(this.leadId).subscribe({
      next: (res: any) => {
        const rows = Array.isArray(res) ? res : res?.rows ?? [];
        this.allItems = rows;
        this.applyFilter();
        this.loading = false;
      },
      error: () => {
        this.allItems = [];
        this.items = [];
        this.loading = false;
      },
    });
  }

  applyFilter(): void {
    const types = FILTER_MAP[this.activityFilter] ?? null;
    let list = [...this.allItems];
    if (types) {
      list = list.filter((item) => {
        if (!types.includes(item.activity_type)) {
          return false;
        }
        if (this.activityFilter === 'email') {
          return /email/i.test(item.activity_description || '');
        }
        if (this.activityFilter === 'call') {
          return /call/i.test(item.activity_description || '');
        }
        return true;
      });
    }
    this.items = list;
  }

  actorName(item: any): string {
    if (item?.activity_type === 'lead_communication_activity') {
      const desc = (item.activity_description || '').toLowerCase();
      if (desc.startsWith('email')) {
        return 'Email';
      }
      if (desc.startsWith('whatsapp')) {
        return 'WhatsApp';
      }
      if (desc.startsWith('call')) {
        return 'Call';
      }
      return 'Communication';
    }

    if (item?.activity_type === 'lead_meeting_activity') {
      return 'Meeting';
    }

    const u = item?.actor || item?.user;
    if (!u) {
      return 'System';
    }
    return (
      [u.prefix, u.fname, u.lname].filter(Boolean).join(' ').trim() || u.email || 'Team member'
    );
  }

  formatWhen(item: any): string {
    const d = item?.createdAt ? new Date(item.createdAt) : null;
    if (!d || Number.isNaN(d.getTime())) {
      return '';
    }
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  initials(item: any): string {
    const name = this.actorName(item);
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return (name[0] || '?').toUpperCase();
  }

  ngOnDestroy(): void {
    this.cleanupTimelineMessageRotation();
  }

  private startTimelineMessageRotation() {
    if (this.timelineMessageInterval) return;
    this.timelineActiveMessages = MODULE_LOADING_MESSAGES['leads'] || MODULE_LOADING_MESSAGES['generic'] || ['Loading...'];
    this.timelineCurrentMessageIndex = 0;
    this.timelineCurrentMessage = this.timelineActiveMessages[this.timelineCurrentMessageIndex] || 'Loading...';
    this.timelineFadeState = '';

    this.timelineMessageInterval = setInterval(() => {
      this.timelineFadeState = 'fade-out';
      setTimeout(() => {
        if (!this.loading) {
          this.cleanupTimelineMessageRotation();
          return;
        }
        this.timelineCurrentMessageIndex = (this.timelineCurrentMessageIndex + 1) % this.timelineActiveMessages.length;
        this.timelineCurrentMessage = this.timelineActiveMessages[this.timelineCurrentMessageIndex];
        this.timelineFadeState = 'fade-in';
        setTimeout(() => {
          this.timelineFadeState = '';
        }, 200);
      }, 200);
    }, 2000);
  }

  private cleanupTimelineMessageRotation() {
    if (this.timelineMessageInterval) {
      clearInterval(this.timelineMessageInterval);
      this.timelineMessageInterval = null;
    }
  }
}
